import { useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Alert, Box, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertySpecialOfferWebApiHandler_create,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { isValidDateStringFormatMMDDYYYY } from '../../../utils/dateUtils';

import SpecialOffer from '../../../model/SpecialOffer';

import DatePickerWithDateSelect from '../../DatePickerWithDateSelect';
import RMWInput from '../../RMWInput';

import type { RentalManagerSpecialOfferInputsDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { UseFieldSaveReturn } from '../../../hooks/useFieldSave';

type Props = {
  defaultOffer?: Omit<RentalManagerSpecialOfferInputsDto, 'listingAlias'>;
  onDescriptionChange?: (value: string) => void;
};

const SpecialOfferFormField = ({
  defaultOffer = {
    description: '',
    startDateYyyyMmDd: '',
    endDateYyyyMmDd: '',
    offerIdEncoded: '',
  },
  onDescriptionChange,
}: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const specialOffer = SpecialOffer(defaultOffer);
  const [customErrorMessage, setCustomErrorMessage] = useState<Record<string, string>>({});

  const { startDateYyyyMmDd, endDateYyyyMmDd } = specialOffer;

  // Create or update mutation based on whether we have an existing offer
  const isEdit = !!defaultOffer.offerIdEncoded;
  const updateMutation = usePropertyEditMutation({
    mutationHook: isEdit
      ? useRentalManagerUserPropertySpecialOfferWebApiHandler_update
      : useRentalManagerUserPropertySpecialOfferWebApiHandler_create,
    onSuccess: (response) => {
      const errors = response.errors as Array<{ name: string; message: string; errorCode: string }> | undefined;
      if (errors?.length) {
        throw new DatarouterError('Server field validation failed', '', 200, {
          errors: errors.map(({ name, message }) => ({ name, message, errorCode: 0 })),
        });
      }
    },
  });

  // Three coordinated fields that all save together
  const descriptionField: UseFieldSaveReturn<string> = useFieldSave({
    fieldName: 'description',
    initialValue: specialOffer.description,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        description: value,
        startDateYyyyMmDd: startDateField.value ? format(new Date(startDateField.value), 'yyyy-MM-dd') : '',
        endDateYyyyMmDd: endDateField.value ? format(new Date(endDateField.value), 'yyyy-MM-dd') : '',
        offerIdEncoded: specialOffer.offerIdEncoded,
        listingAlias,
      },
    }),
  });

  const startDateField: UseFieldSaveReturn<string> = useFieldSave({
    fieldName: 'startDateYyyyMmDd',
    initialValue: startDateYyyyMmDd,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        description: descriptionField.value as string,
        startDateYyyyMmDd: value ? format(new Date(value), 'yyyy-MM-dd') : '',
        endDateYyyyMmDd: endDateField.value ? format(new Date(endDateField.value), 'yyyy-MM-dd') : '',
        offerIdEncoded: specialOffer.offerIdEncoded,
        listingAlias,
      },
    }),
  });

  const endDateField: UseFieldSaveReturn<string> = useFieldSave({
    fieldName: 'endDateYyyyMmDd',
    initialValue: endDateYyyyMmDd,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        description: descriptionField.value as string,
        startDateYyyyMmDd: startDateField.value ? format(new Date(startDateField.value), 'yyyy-MM-dd') : '',
        endDateYyyyMmDd: value ? format(new Date(value), 'yyyy-MM-dd') : '',
        offerIdEncoded: specialOffer.offerIdEncoded,
        listingAlias,
      },
    }),
  });

  const validateDateRange = (startDateValue: string, endDateValue: string) => {
    if (!(startDateValue && endDateValue && isValid(new Date(startDateValue)) && isValid(new Date(endDateValue)))) {
      return;
    }

    const start = new Date(startDateValue);
    const end = new Date(endDateValue);

    if (end < start) {
      setCustomErrorMessage((prev) => ({
        ...prev,
        endDate: 'End date must be after the start date',
      }));
    }
  };

  const handleStartDateChange = (date: string) => {
    if (date && !isValidDateStringFormatMMDDYYYY(date)) {
      setCustomErrorMessage({ ...customErrorMessage, startDate: 'Start date must be in MM/DD/YYYY format' });
      return;
    }

    startDateField.onChange(date);
    setCustomErrorMessage((prev) => ({ ...prev, startDate: '', endDate: '' }));

    validateDateRange(date, endDateField.value as string);
  };

  const handleEndDateChange = (date: string) => {
    if (date && !isValidDateStringFormatMMDDYYYY(date)) {
      setCustomErrorMessage({ ...customErrorMessage, endDate: 'End date must be in MM/DD/YYYY format' });
      return;
    }

    endDateField.onChange(date);
    setCustomErrorMessage((prev) => ({ ...prev, endDate: '' }));

    validateDateRange(startDateField.value as string, date);
  };

  const displayConsolidatedErrorMessage =
    !!startDateField.error &&
    startDateField.error === endDateField.error &&
    startDateField.error === descriptionField.error;

  return (
    <div data-testid="SpecialOfferFormField">
      {displayConsolidatedErrorMessage && (
        <Alert tone="critical" css={{ marginBottom: '400' }}>
          {startDateField.error}
        </Alert>
      )}
      <Paragraph css={{ marginBottom: '400' }}>
        Share details of the deal or discount you’d like to display to renters. This will appear on your listing until
        your chosen end date.
      </Paragraph>

      <FormField
        error={!displayConsolidatedErrorMessage && (!!startDateField.error || !!customErrorMessage.startDate)}
        description={<FormHelp>{startDateField.error || customErrorMessage.startDate}</FormHelp>}
        label={<Label>Offer start date</Label>}
        control={
          <DatePickerWithDateSelect
            onDateChange={handleStartDateChange}
            showNextMonthButton={false}
            initialDate={startDateYyyyMmDd}
            setDefaultDate={false}
            defaultPastDateToToday={false}
            saveOnDateSelect={false}
          />
        }
      />

      <Box css={{ marginBlock: '200' }}>
        <FormField
          error={!displayConsolidatedErrorMessage && (!!endDateField.error || !!customErrorMessage.endDate)}
          description={<FormHelp>{endDateField.error || customErrorMessage.endDate}</FormHelp>}
          label={<Label>Offer end date</Label>}
          control={
            <DatePickerWithDateSelect
              onDateChange={handleEndDateChange}
              showNextMonthButton={false}
              initialDate={endDateYyyyMmDd}
              setDefaultDate={false}
              minDate={(startDateField.value as string) || undefined}
              defaultPastDateToToday={false}
              saveOnDateSelect={false}
            />
          }
        />
      </Box>

      <RMWInput
        data-testid="description-textarea"
        error={!displayConsolidatedErrorMessage && (!!descriptionField.error || !!customErrorMessage.description)}
        isRequired={true}
        errorMessage={
          displayConsolidatedErrorMessage ? undefined : (descriptionField.error ?? customErrorMessage.description)
        }
        description="Your offer won’t post if it includes contact info: phone numbers, URLs, or email addresses"
        fieldName="description"
        flex="1"
        initialValue={specialOffer.description}
        inputType="textarea"
        label="Description"
        maxLength="255"
        showCharacterCount
        onBlur={(value: string | { target: { name: string; value: string } }) => {
          if (typeof value === 'object') {
            value = value.target.value;
          }
          setCustomErrorMessage((prev) => ({ ...prev, description: '' }));
          descriptionField.onChange(value);
          descriptionField.onBlur?.();
          onDescriptionChange?.(value);
        }}
      />
    </div>
  );
};

export default SpecialOfferFormField;
